/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { View } from 'react-native';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { spacing } from '../../shared/theme/spacing';
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketAlertStrip } from '../../shared/ticket/TicketAlertStrip';
import type { ExpenseSettlementListRow } from '../domain/expenseTypes';
import { expenseErpCopy } from '../domain/expenseErpCopy';
import type { ExpensePaymentsHistoryFlow } from '../screens/hooks/useExpensePaymentsHistory';
import { ExpensePaymentHistoryRowCard } from './ExpensePaymentHistoryRowCard';
import { ExpensePaymentsTable } from './ExpensePaymentsTable';

type Props = {
  flow: ExpensePaymentsHistoryFlow;
  layoutWide: boolean;
  onOpenPayment: (row: ExpenseSettlementListRow) => void;
};

export function ExpensePaymentsListEmpty({ flow }: { flow: ExpensePaymentsHistoryFlow }) {
  if (flow.error) {
    return (
      <ModuleEmptyState
        variant="nest"
        title="No pude cargar los pagos"
        subtitle={flow.error}
        primaryAction={{ label: 'Reintentar', onPress: () => void flow.reload() }}
        testID="gastos.payments.empty.error"
      />
    );
  }
  const searching = flow.searchQuery.trim().length > 0;
  const copy = searching
    ? { title: expenseErpCopy.emptyPaymentsSearchTitle, subtitle: expenseErpCopy.emptyPaymentsSearchSubtitle }
    : flow.hasActiveFilters
      ? { title: expenseErpCopy.emptyPaymentsFilteredTitle, subtitle: expenseErpCopy.emptyPaymentsFilteredSubtitle }
      : { title: expenseErpCopy.emptyPaymentsTitle, subtitle: expenseErpCopy.emptyPaymentsSubtitle };
  return (
    <ModuleEmptyState
      variant="nest"
      loading={flow.loading}
      loadingText="Cargando pagos…"
      title={copy.title}
      subtitle={copy.subtitle}
      secondaryAction={
        flow.hasActiveFilters
          ? { label: expenseErpCopy.clearFiltersAction, onPress: flow.clearFilters }
          : undefined
      }
      testID="gastos.payments.empty"
    />
  );
}

export function ExpensePaymentsHistoryPanel({ flow, layoutWide, onOpenPayment }: Props) {
  const listBody =
    flow.rows.length === 0 ? (
      <ExpensePaymentsListEmpty flow={flow} />
    ) : layoutWide ? (
      <ExpensePaymentsTable rows={flow.rows} onOpenPayment={onOpenPayment} />
    ) : (
      <View style={{ gap: 0 }}>
        {flow.rows.map((row) => (
          <ExpensePaymentHistoryRowCard
            key={row.settlementId}
            row={row}
            onPress={() => onOpenPayment(row)}
          />
        ))}
      </View>
    );

  return (
    <View style={{ gap: spacing.sm }}>
      {flow.staleWarning ? (
        <TicketAlertStrip
          title="Pagos desactualizados"
          subtitle={expenseErpCopy.stalePaymentsBanner}
          tone="warn"
          hideChevron
          testID="gastos.payments.stale"
        />
      ) : null}
      <PaperSheet style={{ flex: 1, minHeight: 0 }} testID="gastos.payments.list.sheet">
        {listBody}
      </PaperSheet>
    </View>
  );
}
